import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, ArrowRight } from "lucide-react";
import { fetchServices, fetchCategories } from "../lib/api";
import { motion } from "framer-motion";

const categoryImages = {
  "Threading": "https://images.unsplash.com/photo-1519415387722-a1c3bbef716c?w=800&q=80",
  "Waxing": "https://images.pexels.com/photos/6763618/pexels-photo-6763618.jpeg?w=800",
  "Facial": "https://images.pexels.com/photos/3985338/pexels-photo-3985338.jpeg?w=800&q=80",
};

export const Services = () => {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("All");
  const navigate = useNavigate();

  useEffect(() => {
    fetchServices().then(setServices).catch(() => {});
    fetchCategories().then((c) => setCategories(["All", ...c])).catch(() => {});
  }, []);

  const filtered = active === "All" ? services : services.filter((s) => s.category === active);

  return (
    <div className="pt-10 px-6" data-testid="services-page">
      <p className="script text-2xl text-gold">the menu</p>
      <h1 className="text-4xl font-serif text-espresso mb-1 leading-tight">Our services</h1>
      <p className="text-mocha text-sm mb-6">Handpicked rituals for skin, brows and body.</p>

      {/* Category pills */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-6 px-6 no-scrollbar" data-testid="category-pills">
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setActive(c)}
            data-testid={`category-${c.toLowerCase().replace(/\s/g, '-')}`}
            className={`shrink-0 px-4 py-2 rounded-full text-sm border transition-all ${
              active === c
                ? "bg-espresso text-cream border-espresso shadow-soft"
                : "bg-white text-mocha border-borderSoft hover:border-blush"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Category hero (skip if All) */}
      {active !== "All" && categoryImages[active] && (
        <div className="mt-6 relative rounded-[2rem] overflow-hidden h-40" data-testid="category-hero">
          <img src={categoryImages[active]} alt={active} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-espresso/60 to-transparent flex items-end p-5">
            <h2 className="text-3xl text-cream font-serif">{active}</h2>
          </div>
        </div>
      )}

      {/* Service cards */}
      <div className="mt-6 space-y-3">
        {filtered.map((s, idx) => (
          <motion.div
            key={s.id}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: idx * 0.04 }}
            className="bg-white rounded-[1.75rem] p-5 shadow-soft"
            data-testid={`service-card-${s.id}`}
          >
            <div className="flex justify-between items-start gap-4">
              <div className="flex-1">
                <p className="text-[11px] uppercase tracking-widest text-gold">{s.category}</p>
                <h3 className="text-xl font-serif text-espresso leading-tight mt-1">{s.name}</h3>
                <p className="text-mocha text-sm mt-1.5 leading-relaxed">{s.description}</p>
                <div className="flex items-center gap-1.5 mt-3 text-xs text-mocha">
                  <Clock size={12} />
                  <span>{s.duration} min</span>
                </div>
              </div>
              <div className="text-right shrink-0">
                <p className="text-2xl font-serif text-espresso">${s.price}</p>
              </div>
            </div>
            <button
              onClick={() => navigate(`/book?service=${s.id}`)}
              className="mt-4 w-full flex items-center justify-between bg-creamDeep hover:bg-blush/40 rounded-full pl-5 pr-2 py-2 text-sm text-espresso transition"
              data-testid={`book-${s.id}`}
            >
              <span>Book this</span>
              <span className="w-8 h-8 rounded-full bg-espresso text-cream flex items-center justify-center">
                <ArrowRight size={14} />
              </span>
            </button>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default Services;
